import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  InsightsRunApiError,
  InsightsRunApiFactory,
} from "@/apis/brand-os/insights-run";
import { PaidMediaPromptApiFactory } from "@/apis/brand-os/paid-media-prompts";
import type {
  InsightsRunRequest,
  InsightsRunResponse,
} from "@/schema/brand-os/insights-run";

/**
 * Hook for `POST /api/v1/ads-intelligence/insights/run`.
 *
 * Runs are cached client-side via the TanStack Query cache, keyed on
 * Full request identity: `user_id`, `prompt`, `deep_research`, `organization_id`,
 * `customer_id`, `google_customer_id`, `currency` (see `insightsRunQueryKey`).
 *
 *   - Identical re-submits return instantly from cache (no LLM call).
 *   - Results survive remounts / route navigation up to `gcTime`.
 *   - Picking a saved prompt with a recent cached run can short-circuit
 *     to the cached result via `getCached(...)` (see `InsightsSection`).
 *
 * `staleTime` is 5 min — long enough to dedupe spam-clicking "Run", but
 * short enough that a user returning ~10 min later gets fresh data.
 * `gcTime` is 30 min so the result sticks around through routine
 * navigation between the page and chat / playbook etc.
 *
 * Side-effect on every *fresh* fetch: the backend auto-upserts a
 * `SavedPrompt` for `(prompt, user_id)`. We invalidate the prompts
 * list query on first-fetch success so the drawer reflects the new
 * row. Cached hits skip this — no new save happened on the backend.
 *
 * Auto-retry is intentionally OFF (`fetchQuery` defaults to no retry
 * via `retry: false` below). Runs cost real LLM tokens; the failure
 * modes here benefit from human judgement (rephrase) rather than
 * silent re-spend.
 */

const STALE_MS = 5 * 60_000;
const GC_MS = 30 * 60_000;

/** Align with backend FRAMEWORK_VERSION when structured JSON shape changes. */
const STRUCTURED_CONTRACT_VERSION = 22;

/**
 * Cache key builder. Exported so consumers (e.g. the section, when the
 * user picks a saved prompt) can peek at cached results without going
 * through a full `run()` round-trip.
 *
 * `refresh` is intentionally NOT part of the key — it's a control flag
 * that bypasses the cache, not a different request identity. Including
 * it would create two cache entries for the same logical run.
 */
export function insightsRunQueryKey(req: InsightsRunRequest) {
  return [
    "insights-run",
    STRUCTURED_CONTRACT_VERSION,
    req.user_id ?? "anon",
    req.prompt.trim(),
    Boolean(req.deep_research),
    req.organization_id ?? "",
    req.customer_id ?? "",
    req.google_customer_id ?? "",
    req.currency ?? "",
  ] as const;
}

export function useInsightsRun() {
  const queryClient = useQueryClient();
  const [data, setData] = useState<InsightsRunResponse | null>(null);
  const [error, setError] = useState<InsightsRunApiError | null>(null);
  const [isPending, setIsPending] = useState(false);

  /**
   * Synchronously look up a cached result without firing a request.
   * Returns `undefined` if there's no entry under the derived key.
   *
   * Useful for "picked a saved prompt" UX: hydrate the result panel
   * with whatever we've already got cached for that prompt's text
   * (often nothing on first session, but a real cache hit on a
   * second session as long as it's within `gcTime`).
   */
  const getCached = useCallback(
    (req: InsightsRunRequest): InsightsRunResponse | undefined => {
      return queryClient.getQueryData<InsightsRunResponse>(insightsRunQueryKey(req));
    },
    [queryClient],
  );

  const run = useCallback(
    async (req: InsightsRunRequest) => {
      const trimmed = req.prompt.trim();
      if (!trimmed) return;
      const refresh = Boolean(req.refresh);
      const normalized: InsightsRunRequest = { ...req, prompt: trimmed, refresh };
      const key = insightsRunQueryKey(normalized);

      // refresh:true → drop both the FE cache entry and the visible
      // result so we don't show stale data while the new run is in
      // flight, and so `fetchQuery` actually hits the network instead
      // of returning the still-warm entry.
      if (refresh) {
        queryClient.removeQueries({ queryKey: key });
      }

      const wasCached = !refresh && queryClient.getQueryData(key) !== undefined;

      setError(null);
      // If cache hit, surface it immediately while `fetchQuery` short-
      // circuits — keeps the UI from flashing the loading skeleton on
      // repeat runs of the same prompt.
      if (wasCached) {
        const cached = queryClient.getQueryData<InsightsRunResponse>(key);
        if (cached) setData(cached);
      } else {
        // refresh paths also clear `data` to avoid flashing the prior
        // result during the fresh run (would feel like the refresh
        // didn't take).
        if (refresh) setData(null);
        setIsPending(true);
      }

      try {
        const result = await queryClient.fetchQuery<
          InsightsRunResponse,
          InsightsRunApiError
        >({
          queryKey: key,
          queryFn: () => InsightsRunApiFactory.Apis.Run(normalized),
          staleTime: STALE_MS,
          gcTime: GC_MS,
          retry: false,
        });
        setData(result);
        // Bust the saved-prompts list only on a *fresh* fetch — the
        // backend only creates/touches a SavedPrompt when /run was
        // actually called. Cache hits skip this entirely.
        if (!wasCached && normalized.user_id) {
          queryClient.invalidateQueries({
            queryKey: PaidMediaPromptApiFactory.Keys.List(normalized.user_id),
          });
        }
      } catch (e) {
        setError(e as InsightsRunApiError);
      } finally {
        setIsPending(false);
      }
    },
    [queryClient],
  );

  /**
   * Clears the *displayed* result. Does NOT purge the cache — picking
   * the same prompt again can still hot-hit via `getCached()`.
   *
   * Used when the user starts editing the textarea (the result that's
   * showing no longer corresponds to the prompt they're now writing).
   */
  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setIsPending(false);
  }, []);

  /**
   * Hydrate the displayed result from a known response (e.g. one
   * already returned by `getCached()`). Avoids an awkward dance of
   * "look up cache → if found, run() to populate state via fetchQuery's
   * fast path → which still hits the network on staletime expiry".
   */
  const hydrate = useCallback((response: InsightsRunResponse) => {
    setData(response);
    setError(null);
    setIsPending(false);
  }, []);

  return {
    run,
    data,
    error,
    isPending,
    isSuccess: data !== null && error === null,
    isError: error !== null,
    reset,
    hydrate,
    getCached,
  };
}
